import React, { useState, useRef, useLayoutEffect, useCallback } from "react";
import { Box, Chip } from "@mui/material";

const getUniqueLineCount = (elements) => {
  const tops = [];
  elements.forEach((el) => {
    const top = el.offsetTop;
    if (!tops.some((t) => Math.abs(t - top) < 2)) {
      tops.push(top);
    }
  });
  return tops.length;
};

const TechStackChipList = ({ techStackList }) => {
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(techStackList.length);
  const measureRef = useRef(null);
  const visibleRef = useRef(null);

  const measureFromHidden = useCallback(() => {
    if (!measureRef.current) return techStackList.length;

    const chips = measureRef.current.querySelectorAll("[data-tech-chip]");
    if (chips.length === 0) return techStackList.length;

    const lineTops = [];
    for (let i = 0; i < chips.length; i += 1) {
      const top = chips[i].offsetTop;
      if (!lineTops.some((t) => Math.abs(t - top) < 2)) {
        lineTops.push(top);
      }
      if (lineTops.length > 2) {
        return Math.max(1, i);
      }
    }

    return techStackList.length;
  }, [techStackList.length]);

  const runMeasurement = useCallback(() => {
    if (expanded) return;
    setVisibleCount(measureFromHidden());
  }, [expanded, measureFromHidden]);

  useLayoutEffect(() => {
    setExpanded(false);
    setVisibleCount(techStackList.length);
  }, [techStackList]);

  useLayoutEffect(() => {
    runMeasurement();
  }, [runMeasurement, techStackList]);

  useLayoutEffect(() => {
    if (expanded) return undefined;

    const target = measureRef.current;
    if (!target) return undefined;

    const observer = new ResizeObserver(() => runMeasurement());
    observer.observe(target);
    return () => observer.disconnect();
  }, [expanded, runMeasurement]);

  useLayoutEffect(() => {
    if (expanded || visibleCount >= techStackList.length || !visibleRef.current) {
      return;
    }

    const chips = visibleRef.current.querySelectorAll("[data-tech-chip]");
    const moreChip = visibleRef.current.querySelector("[data-more-chip]");
    if (!moreChip || chips.length === 0) return;

    if (getUniqueLineCount([...chips, moreChip]) > 2 && visibleCount > 1) {
      setVisibleCount((current) => Math.max(1, current - 1));
    }
  }, [expanded, visibleCount, techStackList.length]);

  if (!techStackList || techStackList.length === 0) {
    return null;
  }

  const hasOverflow = !expanded && visibleCount < techStackList.length;
  const displayed = expanded ? techStackList : techStackList.slice(0, visibleCount);

  return (
    <Box sx={{ position: "relative", mt: 1, width: "100%", overflow: "hidden" }}>
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: "100%",
          height: 0,
          overflow: "hidden",
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          top: 0,
          left: 0,
        }}
      >
        {techStackList.map((tech) => (
          <Chip
            key={`measure-${tech}`}
            data-tech-chip
            label={tech}
            size="small"
            color="secondary"
            variant="outlined"
          />
        ))}
      </Box>

      <Box
        ref={visibleRef}
        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}
      >
        {displayed.map((tech) => (
          <Chip
            key={tech}
            data-tech-chip
            label={tech}
            size="small"
            color="secondary"
            variant="outlined"
          />
        ))}
        {hasOverflow && (
          <Chip
            data-more-chip
            label="..."
            size="small"
            color="secondary"
            variant="outlined"
            onClick={() => setExpanded(true)}
            sx={{ cursor: "pointer" }}
          />
        )}
      </Box>
    </Box>
  );
};

export default TechStackChipList;
